import { useState } from 'react';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '@clay-run/glaze/icon';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@clay-run/glaze/table';
import { Text } from '@clay-run/glaze/text';
import { useApi } from '../../../lib/api';
import { fetchJson } from '../../../lib/apiFetchJson';
import { RecentSendsResponseSchema, type RecentSend } from '@shared/flow/recent-sends';
import { FLOW_ICONS } from '../icons';

/**
 * Recent-sends warning bar + history accordion (Warm Intro plan U11, R5/R9).
 *
 * Reference rendering = the APPROVED prototype
 * `docs/flow/warm-intro-recent-send-prototype.html` (380px docked-sidebar
 * form factor, `.warnbar` + `.hist` accordion structure), reskinned onto
 * Glaze (rail-glaze-parity, U7): the prototype's one-off `tangerine-*` hexes
 * are gone — the bar now reads through Clay's semantic tokens
 * (`bg-bg-orange-light`/`text-content-warning`), and the history table is a
 * real `@clay-run/glaze` `Table`, matching `CampaignHistoryTable.tsx`'s
 * precedent for this same component family.
 *
 * R5 (warn, never block): purely informational. Nothing here disables
 * Send (U2 buttonless-editor redesign removed "Mark ready" — see
 * `EnrollmentItemEditor`'s Send button / `itemBlockReason`), untouched by
 * this bead.
 *
 * Data: self-fetches exactly like `WarningBadges` — same query shape, same
 * cache-indefinitely rule (nothing on the client mutates send history).
 *
 * D18 (flow-contacts-bulk-send QA round 3) deleted the OTHER mount that used
 * to share this query key — `AssignedRow`'s always-mounted "orange dot"
 * (`ContactRowMeta`), which fired this same fetch once per rendered row
 * purely to light a 6px indicator, superseded by a text signal derived from
 * a different, item-derived predicate. This bar (`useRecentSendsForItem`,
 * exported below) is now the ONLY consumer. That matters for the query's own
 * `refetchOnWindowFocus: 'always'` self-heal (see the option's comment): it
 * only fires while an observer is subscribed, and before D18 the row's
 * always-mounted dot GUARANTEED one was subscribed any time the row was
 * visible at all. Now the only observer is this bar, mounted only while its
 * editor is open — so `refetchOnMount: 'always'` (below) is what keeps this
 * data honest across an open→close→(external send)→reopen sequence within
 * one session, since the shared query key can no longer rely on an ambient
 * always-on observer to have refreshed it in the meantime.
 */

/** Shared query key — see the module doc comment above for why this bar is now the sole caller. */
function recentSendsQueryKey(itemId: number) {
  return ['flow-queue-recent-sends', itemId] as const;
}

export function useRecentSendsForItem(itemId: number) {
  const { apiFetch } = useApi();
  return useQuery<RecentSend[]>({
    queryKey: recentSendsQueryKey(itemId),
    queryFn: () => fetchJson(apiFetch, RecentSendsResponseSchema, `/api/flow/queue/items/${itemId}/recent-sends`),
    // Fix 7 (MINOR bd finding): recent-sends is a 30-day ROLLING window
    // (`RecentSend`s older than 30 days age OUT of the endpoint's own
    // response, not just off some client display filter) — `staleTime:
    // Infinity` alone (WarningBadges' posture) kept showing a send that
    // crossed the 30-day boundary since the last fetch until an unrelated
    // reload.
    //
    // A numeric TTL (e.g. 5 min) is what the original finding asked for, but
    // the repo's `terra/no-ttl-literals` lint rule (.oxlintrc.json, `error`,
    // no override for this directory) hard-bans any numeric `staleTime` —
    // only `Infinity` or `0` pass, and `0` breaks the dedup contract this
    // query already relies on (`QueueBody`'s compact dot and this bar share
    // ONE request via the identical query key — see the module doc comment
    // above; `0` would make the SECOND mount's subscription see the
    // first's cached result as immediately stale and re-fetch, splitting
    // that one request back into two — pinned by the existing "no duplicate
    // fetch" test in EnrollQueue.test.tsx).
    //
    // `refetchOnWindowFocus: 'always'` is the compliant fix: unlike plain
    // `true` (gated behind staleness — a no-op under `Infinity`), `'always'`
    // bypasses staleTime entirely and refetches on every window refocus —
    // exactly the "advisory recency data, cheap endpoint" self-heal this fix
    // wants (the moment a rep is most likely to actually look at this),
    // without touching `staleTime` (so dedup across simultaneous mounts is
    // untouched) and without a banned numeric literal.
    //
    // `refetchOnMount: 'always'` (Codex adversarial follow-up, D18 round 3
    // batch) — added when D18 deleted this query's other, always-mounted
    // consumer (see the module doc comment above). `refetchOnWindowFocus`
    // alone now self-heals only while THIS bar happens to already be open at
    // the moment of a window refocus — a much narrower window than before,
    // when the row's dot guaranteed an observer any time the row was simply
    // visible. `refetchOnMount: 'always'` closes that gap the same
    // "bypasses staleTime, no banned literal" way: every time a rep opens an
    // item's editor (the only remaining trigger for this fetch at all,
    // post-D18), it re-fetches unconditionally rather than trusting a
    // `staleTime: Infinity` cache that nothing else in this session
    // necessarily kept fresh.
    staleTime: Infinity,
    refetchOnWindowFocus: 'always',
    refetchOnMount: 'always',
  });
}

function daysAgo(occurredAt: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(occurredAt).getTime()) / 86_400_000));
}

export function RecentSendsBar({ itemId }: { itemId: number }) {
  const [open, setOpen] = useState(false);
  const { data } = useRecentSendsForItem(itemId);

  // No sends in the 30-day window -> no bar at all (prototype's "clean"
  // contrast case; R5 — this is informational, so absence of a signal is
  // absence of UI, never a placeholder).
  if (!data || data.length === 0) return null;

  // Date-desc regardless of the fetch's own ordering — the endpoint
  // contract promises newest-first (`RecentSendsResponseSchema`'s doc
  // comment), but the accordion's correctness shouldn't depend on that
  // promise never drifting.
  const sorted = [...data].sort(
    (a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime(),
  );
  const latest = sorted[0];
  const prefix = latest.source === 'playhouse' ? 'Recent Playhouse invite' : 'Recent queue send';
  const count = data.length;

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-label={`Toggle recent sends history (${count} recent send${count === 1 ? '' : 's'})`}
        className="flex w-full cursor-pointer items-center gap-2 rounded-lg border-[0.5px] border-border-primary bg-bg-orange-light px-3 py-2 text-left text-xs text-content-warning"
      >
        <span className="min-w-0 flex-1 truncate">
          {prefix} — <b className="font-semibold">{latest.label}</b> ({daysAgo(latest.occurred_at)}d ago)
        </span>
        <span className="whitespace-nowrap text-xs font-semibold">
          {count} recent send{count === 1 ? '' : 's'}
        </span>
        <Icon
          type={FLOW_ICONS.expand}
          size="xs"
          aria-hidden
          className={clsx('transition-transform', open && 'rotate-90')}
        />
      </button>

      {open && (
        <div className="mt-2 max-h-[280px] overflow-y-auto rounded-lg bg-bg-secondary p-3">
          <Text render={<h3 />} size="xs" variant="tertiary" weight="medium" className="mb-2 uppercase tracking-wide">
            Recent sends · last 30 days
          </Text>
          {/* QA round 3 (Spencer): pared from 4 columns (Send/Source/When/
              Status) to 2 — just the play name + days ago. Source and Status
              never fit legibly at 380px, and the label already names the
              play; full per-send detail lives in the Requests feed and the
              execution row, not this glanceable warning. */}
          <Table dense>
            <TableHead>
              <TableRow>
                <TableHeader>Play</TableHeader>
                <TableHeader>When</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map(send => (
                <TableRow key={`${send.source}-${send.slug ?? ''}-${send.occurred_at}-${send.label}`}>
                  <TableCell title={send.label} className="max-w-0 truncate">
                    {send.label}
                  </TableCell>
                  <TableCell>{daysAgo(send.occurred_at)}d ago</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
